/** Multi-pin, selectable location map. Coordinates are data, never guessed.
 *
 *  The journey's location step used to draw a CSS-gradient canvas with pins at
 *  hardcoded percentages (`top: [46, 24, 70, …][i % 8]%`), which put a campus
 *  wherever its array index landed — a picture of a map rather than a map. The
 *  tenant's location rows carry real `latitude`/`longitude` (platform PR #1888 /
 *  map-sync 07c1ae419); this renders those.
 *
 *  Sibling of `screens/locations/LocationMap.tsx` and deliberately the same
 *  pairing — `MapFrame` for the four states, `GoogleMap` for the pins — so the
 *  two surfaces cannot disagree about what an unlocated place looks like. The
 *  only differences are N markers instead of one, and selection.
 *
 *  NOTE FOR CD: if PR #1888 already ships a `LocationsMap`, delete this file and
 *  repoint the import in `AllJourneys.tsx`. The load-bearing parts of the
 *  correction are the preserved coordinates, the `mapLocations` projection and
 *  the call site — not this component.
 */
import { useCallback, useMemo, useState } from "react";
import { MapFrame } from "@v0y4ge/jrni-ui";
import GoogleMap from "./GoogleMap";
import { useT } from "../../lib/i18n";

/** The browser Google Maps key this bundle was actually served with.
 *
 *  Studio is a Vite build, so the key arrives as `VITE_GOOGLE_MAPS_API_KEY`.
 *  The public booking page is a generic prebuilt bundle with no tenant build
 *  env at all; there the host page carries the tenant's browser key at runtime
 *  in `<meta name="jrni-google-maps-api-key">`. The build value wins when it is
 *  present, the page's meta tag is read otherwise, and with neither the result
 *  is `undefined` — `GoogleMap` then reports "not configured" and `MapFrame`
 *  shows its error state, exactly as before. Never a placeholder key. */
function resolveGoogleMapsApiKey(): string | undefined {
  const built = String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "").trim();
  if (built) return built;
  if (typeof document === "undefined") return undefined;
  const served = document.querySelector<HTMLMetaElement>('meta[name="jrni-google-maps-api-key"]')?.content?.trim();
  return served || undefined;
}

export interface LocationsMapPin {
  id: string;
  latitude?: number;
  longitude?: number;
  label?: string;
  selected?: boolean;
}

export interface LocationsMapProps {
  pins: LocationsMapPin[];
  height?: number;
  caption?: string;
  actions?: React.ReactNode;
}

/* No pin-click selection. `GoogleMap` exposes no marker-click callback, and
   adding one would widen this correction into a shared map component the build
   report asked us not to touch. Selection stays on the location LIST beside the
   map, where it already works; the selected row centres the view. */
export default function LocationsMap({ pins, height = 220, caption, actions }: LocationsMapProps) {
  const t = useT();
  // The host page's head is in place before the bundle runs, so one read per mount.
  const apiKey = useMemo(() => resolveGoogleMapsApiKey(), []);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);

  /* Only located places are pinned. A row without coordinates is omitted rather
     than placed at 0,0 — which is a real position in the Gulf of Guinea — and
     the caller still lists it, so it stays bookable. */
  const located = useMemo(
    () => pins.filter((pin) =>
      typeof pin.latitude === "number" && typeof pin.longitude === "number"
      && Number.isFinite(pin.latitude) && Number.isFinite(pin.longitude)),
    [pins],
  );
  const markers = useMemo(
    () => located.map((pin) => ({
      id: pin.id,
      latitude: pin.latitude as number,
      longitude: pin.longitude as number,
      label: pin.label,
    })),
    [located],
  );
  /* The selected pin centres the view, so choosing a campus in the list moves
     the map to it. Absent a selection the map frames the first located row. */
  const center = useMemo(() => {
    const chosen = located.filter((pin) => pin.selected)[0] || located[0];
    return chosen ? { latitude: chosen.latitude as number, longitude: chosen.longitude as number } : undefined;
  }, [located]);

  const onStatusChange = useCallback((nextStatus: "loading" | "ready" | "error", message?: string) => {
    setStatus(nextStatus);
    setError(message ?? null);
  }, []);

  return (
    <MapFrame
      state={located.length ? status : "empty"}
      height={height}
      ariaLabel={t("journeys.map.locationsMap", "Locations map")}
      caption={caption}
      actions={actions}
      loadingLabel={t("journeys.map.loadingMap", "Loading map…")}
      errorTitle={t("journeys.map.theMapCouldnTLoad", "The map couldn't load.")}
      errorDescription={error ?? "Google Maps could not be loaded."}
      emptyTitle={t("journeys.map.nothingToPin", "Nothing to pin yet")}
      emptyDescription={t("journeys.map.noLocationHasCoordinates", "None of these locations has coordinates on record, so there is nothing to place. Locate them from their addresses in Campuses.")}
    >
      {located.length ? (
        <div inert={status !== "ready"}>
          <GoogleMap
            apiKey={apiKey}
            mapId={import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID"}
            markers={markers}
            center={center}
            height={height}
            onStatusChange={onStatusChange}
          />
        </div>
      ) : null}
    </MapFrame>
  );
}
